import React, { useState, useEffect, useRef, useMemo } from 'react';
import { comprehensiveCreativeAnalysis } from '../../services/comprehensiveCreativeAnalysis';
import { useAuth } from '../../contexts/AuthContext';
import GroupTimeSeriesChart from '../GroupTimeSeriesChart';
import ComparisonChart from '../ComparisonChart';
import ReportSummary from '../ReportSummary';
import { Folder, TrendingUp, TrendingDown } from 'lucide-react';
import genericClusteringService from '../../services/genericClusteringService';

const AdTypeAnalysis = ({ filters, onFilterChange }) => {
  const { getAccountId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [adTypeData, setAdTypeData] = useState([]);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [adsModal, setAdsModal] = useState({ isOpen: false, adType: null, ads: [], loading: false });
  const [sampleAdsByType, setSampleAdsByType] = useState({});
  const [viewMode, setViewMode] = useState('card'); // 'card', 'time-trend', 'comparison'
  const requestIdRef = useRef(0);
  
  // Use correlation filter from global Dashboard Configuration
  const correlationMetric = filters?.correlationMetric || 'roas';

  // Generate date range condition from filters
  const getDateRange = () => {
    // Check for custom date range first
    if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
      console.log('🔄 AdTypeAnalysis using CUSTOM date range:', filters.startDate, 'to', filters.endDate);
      return `adp.date >= '${filters.startDate}' AND adp.date <= '${filters.endDate}'`;
    }
    // Fall back to preset days - use timezone-aware CURRENT_DATE to match time series chart
    if (filters?.dateRange && typeof filters.dateRange === 'number') {
      console.log('🔄 AdTypeAnalysis using PRESET date range:', filters.dateRange, 'days');
      return `adp.date >= (CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${filters.dateRange} days'`;
    }
    return `adp.date >= (CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '7 days'`;
  };

  // Generate ad name filter from filters
  const getAdNameFilter = () => {
    if (filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0) {
      // Return the proper object format for the service
      return {
        keywords: filters.adNameFilterKeywords.filter(k => k.trim()),
        logic: filters.adNameFilterLogic || 'AND',
        mode: filters.adNameFilterMode || 'include'
      };
    }
    return null;
  };

  // Load ad type analysis data
  const loadAdTypeData = async () => {
    const accountId = getAccountId();
    if (!accountId) {
      return;
    }

    // Prevent multiple simultaneous requests
    if (loading) {
      return;
    }

    // Generate unique request ID to handle race conditions
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    try {
      const dateRange = getDateRange();
      const adNameFilter = getAdNameFilter();
      
      // Try to get cached result first (include correlationMetric in cache key)
      const cacheResponse = await fetch('/api/cache/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId,
          analysisType: `ad_type_analysis_${correlationMetric}`,
          dateRangeDays: filters?.dateRange || 7,
          adNameFilter: adNameFilter
        })
      });

      if (cacheResponse.ok) {
        const cacheResult = await cacheResponse.json();
        
        if (cacheResult.success && cacheResult.cached && cacheResult.data) {
          // Check if cached data contains errors or empty data
          const hasError = cacheResult.data.error || cacheResult.data.success === false;
          const hasValidData = Array.isArray(cacheResult.data) && cacheResult.data.length > 0;
          
          if (!hasError && hasValidData) {
            // Use cached data only if it's valid
            if (currentRequestId === requestIdRef.current) {
              setAdTypeData(cacheResult.data);
              setLoading(false);
            }
            return;
          }
          // If cached data has errors or is empty, continue to fresh analysis
        }
      }

      // Cache miss - run expensive analysis
      const startTime = Date.now();
      const data = await comprehensiveCreativeAnalysis.getAdTypeAnalysis(
        accountId, 
        dateRange, 
        adNameFilter,
        correlationMetric
      );
      const generationTime = Date.now() - startTime;

      // Store in cache for future use (include correlationMetric in cache key)
      fetch('/api/cache/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId,
          analysisType: `ad_type_analysis_${correlationMetric}`,
          dateRangeDays: filters?.dateRange || 7,
          adNameFilter: adNameFilter,
          analysisData: data,
          generationTimeMs: generationTime
        })
      }).catch(err => console.warn('Failed to cache ad type analysis:', err));

      // Only update if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setAdTypeData(data);
      }
    } catch (error) {
      console.error('❌ Error loading ad type analysis:', error);
      // Don't clear data on error, keep previous data
    } finally {
      // Only set loading to false if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Handle chat button click for specific ad type (similar to TimeTrends pattern)
  const handleAdTypeChat = (adType, isTopPerformer = true) => {
    const dateRangeText = filters?.dateRange ? `last ${filters.dateRange} days` : 'last 7 days';
    const keywordsText = filters?.adNameFilterKeywords?.length > 0 
      ? ` with keywords: ${filters.adNameFilterKeywords.join(', ')}`
      : '';
    
    const performanceText = isTopPerformer ? 'top performing' : 'underperforming';
    const metricValue = correlationMetric === 'revenue' ? `$${parseFloat(adType.total_revenue || 0).toFixed(2)} revenue` :
                       correlationMetric === 'ctr' ? `${parseFloat(adType.ctr || 0).toFixed(2)}% CTR` :
                       correlationMetric === 'spend' ? `$${parseFloat(adType.total_spend || 0).toFixed(2)} spend` :
                       correlationMetric === 'cpa' ? `$${parseFloat(adType.cpa || 0).toFixed(2)} CPA` :
                       `${parseFloat(adType.roas || 0).toFixed(2)}x ROAS`;
    
    const prompt = `Analyze this ${performanceText} ad type: "${adType.ad_type}" - it has ${adType.number_of_ads || 0} ads, ${metricValue}, $${parseFloat(adType.total_spend || 0).toFixed(2)} total spend, ${parseFloat(adType.roas || 0).toFixed(2)}x ROAS, ${parseFloat(adType.ctr || 0).toFixed(2)}% CTR over the ${dateRangeText}${keywordsText}. Why is this ad type ${isTopPerformer ? 'performing well' : 'underperforming'} and what creative strategies should I use for ${adType.ad_type} ads?`;
    
    // Auto-populate the chat input and open chat (handled by parent Dashboard component)
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange({ type: 'chat', message: prompt });
    }
  };

  useEffect(() => {
    if (!filters) {
      console.log('⏳ AdTypeAnalysis waiting for filters...');
      return;
    }
    console.log('🔄 AdTypeAnalysis filters changed, reloading...', filters);
    loadAdTypeData();
  }, [filters]);

  // Filter out unknown or non-applicable ad types for cleaner UI
  const filteredAdTypeData = adTypeData.filter(type => 
    type.ad_type && 
    type.ad_type !== 'Unknown' && 
    type.ad_type !== 'N/A' && 
    type.ad_type !== 'n/a' && 
    !type.ad_type.toLowerCase().includes('unknown') &&
    !type.ad_type.toLowerCase().includes('n/a') &&
    !type.ad_type.toLowerCase().includes('not applicable')
  );

  // Calculate outliers (top performers and underperformers)
  const outlierAnalysis = useMemo(() => {
    if (filteredAdTypeData.length === 0) return { topPerformers: [], underperformers: [] };

    // Get the metric values based on correlationMetric
    const metricValues = filteredAdTypeData.map(type => {
      if (correlationMetric === 'revenue') return parseFloat(type.total_revenue || 0);
      if (correlationMetric === 'ctr') return parseFloat(type.ctr || 0);
      if (correlationMetric === 'spend') return parseFloat(type.total_spend || 0);
      if (correlationMetric === 'cpa') return parseFloat(type.cpa || 0);
      return parseFloat(type.roas || 0);
    }).filter(v => v > 0);

    if (metricValues.length === 0) return { topPerformers: [], underperformers: [] };

    // Calculate statistics
    const sorted = [...metricValues].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;
    const upperBound = q3 + (1.5 * iqr);
    const lowerBound = q1 - (1.5 * iqr);
    const median = sorted[Math.floor(sorted.length / 2)];
    const mean = metricValues.reduce((a, b) => a + b, 0) / metricValues.length;
    const stdDev = Math.sqrt(metricValues.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / metricValues.length);

    // Identify outliers
    const topPerformers = [];
    const underperformers = [];

    filteredAdTypeData.forEach(type => {
      const value = correlationMetric === 'revenue' ? parseFloat(type.total_revenue || 0) :
                    correlationMetric === 'ctr' ? parseFloat(type.ctr || 0) :
                    correlationMetric === 'spend' ? parseFloat(type.total_spend || 0) :
                    correlationMetric === 'cpa' ? parseFloat(type.cpa || 0) :
                    parseFloat(type.roas || 0);

      // Top performer: value > upperBound OR value > mean + 1.5*stdDev
      if (value > Math.max(upperBound, mean + 1.5 * stdDev)) {
        topPerformers.push(type);
      }
      // Underperformer: value < lowerBound OR value < mean - 1.5*stdDev (and value > 0)
      else if (value > 0 && value < Math.min(lowerBound, mean - 1.5 * stdDev)) {
        underperformers.push(type);
      }
    });

    return { topPerformers, underperformers, median, mean };
  }, [filteredAdTypeData, correlationMetric]);

  // Check if an ad type is an outlier
  const isTopPerformer = (type) => {
    return outlierAnalysis.topPerformers.some(t => 
      t.ad_type === type.ad_type && t.media_type === type.media_type
    );
  };

  const isUnderperformer = (type) => {
    return outlierAnalysis.underperformers.some(t => 
      t.ad_type === type.ad_type && t.media_type === type.media_type
    );
  };

  // Fetch sample ads for an ad type - optimized to use API endpoint directly
  const fetchSampleAdsForType = async (adType, adIds) => {
    if (!adIds || adIds.length === 0) return null;
    
    try {
      // Use the optimized API endpoint that filters by ad IDs server-side
      // Get just 1 ad with an asset (we'll filter for assets client-side if needed)
      const topAds = await genericClusteringService.getTopAdsByMetric(
        adIds, 
        10, // Get top 10 to find one with an asset
        correlationMetric, 
        filters?.dateRange
      );
      
      // Find first ad with an asset
      const adWithAsset = topAds.find(ad => ad.asset_url);
      if (adWithAsset) {
        return {
          ad_name: adWithAsset.ad_name,
          asset_url: adWithAsset.asset_url,
          image_url: adWithAsset.asset_url,
          video_url: adWithAsset.asset_url,
          media_format: adWithAsset.media_format,
          raw_ad_id: adWithAsset.raw_ad_id
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching sample ad:', error);
      return null;
    }
  };

  // Load sample ads for all ad types
  useEffect(() => {
    if (adTypeData.length > 0) {
      adTypeData.forEach(async (type) => {
        const key = `${type.ad_type}_${type.media_type}`;
        if (!sampleAdsByType[key] && type.ad_ids && type.ad_ids.length > 0) {
          const sampleAd = await fetchSampleAdsForType(type.ad_type, type.ad_ids);
          if (sampleAd) {
            setSampleAdsByType(prev => ({
              ...prev,
              [key]: sampleAd
            }));
          }
        }
      });
    }
  }, [adTypeData]);

  // Open modal to view all ads for an ad type - fetch ALL ads including those without performance data
  const openAdsModal = async (adType) => {
    if (!adType.ad_ids || adType.ad_ids.length === 0) return;
    
    const accountId = getAccountId();
    if (!accountId) return;
    
    try {
      // Show modal immediately (even if empty) to provide instant feedback
      setAdsModal({
        isOpen: true,
        adType: adType,
        ads: [],
        loading: true
      });
      
      // Deduplicate input ad_ids first to ensure we request all unique ads
      const uniqueAdIds = [...new Set(adType.ad_ids.map(id => typeof id === 'string' ? id : (id.raw_ad_id || id.ad_id || id)).filter(Boolean))];
      
      console.log('🔍 AdTypeAnalysis - Opening modal:', {
        adType: adType.ad_type,
        mediaType: adType.media_type,
        totalAdIds: adType.ad_ids.length,
        uniqueAdIds: uniqueAdIds.length
      });
      
      // Fetch performance data first (this includes asset_url for ads with performance)
      // Pass the same filters used in ad type analysis to ensure data consistency
      let performanceData = [];
      try {
        performanceData = await genericClusteringService.getTopAdsByMetric(
          uniqueAdIds, 
          uniqueAdIds.length,
          correlationMetric, 
          filters?.dateRange,
          {
            dateRangeType: filters?.dateRangeType,
            startDate: filters?.startDate,
            endDate: filters?.endDate,
            adNameFilter: filters?.adNameFilterKeywords ? {
              keywords: filters.adNameFilterKeywords,
              logic: filters.adNameFilterLogic || 'AND',
              mode: filters.adNameFilterMode || 'include'
            } : null
          }
        );
      } catch (perfError) {
        console.warn('Could not fetch performance data:', perfError);
      }
      
      // Fetch creative data for all ad IDs (for text content)
      let creativeData = [];
      try {
        creativeData = await genericClusteringService.getCreativeData(uniqueAdIds);
      } catch (creativeError) {
        console.warn('Could not fetch creative data:', creativeError);
      }
      
      console.log('🔍 AdTypeAnalysis - Data returned:', {
        requested: uniqueAdIds.length,
        performanceData: performanceData.length,
        creativeData: creativeData.length
      });
      
      // Create maps for easy lookup
      const perfMap = new Map();
      performanceData.forEach(ad => {
        const adId = ad.raw_ad_id || ad.ad_id;
        if (adId) {
          perfMap.set(String(adId), ad);
        }
      });
      
      const creativeMap = new Map();
      creativeData.forEach(creative => {
        const adId = creative.raw_ad_id || creative.ad_id;
        if (adId) {
          creativeMap.set(String(adId), creative);
        }
      });
      
      // Start with performance data (has asset URLs), then add ads without performance
      const allAdsMap = new Map();
      
      // Add all ads from performance data (these have asset URLs)
      performanceData.forEach(ad => {
        const adId = String(ad.raw_ad_id || ad.ad_id);
        const creative = creativeMap.get(adId);
        allAdsMap.set(adId, {
          raw_ad_id: ad.raw_ad_id || ad.ad_id,
          ad_id: ad.raw_ad_id || ad.ad_id,
          ad_name: ad.ad_name || 'Unknown Ad',
          asset_url: ad.asset_url, // This comes from performance data
          image_url: ad.asset_url,
          video_url: ad.asset_url,
          media_format: ad.media_format || 'Unknown',
          roas: parseFloat(ad.roas || 0),
          total_spend: parseFloat(ad.total_spend || 0),
          total_revenue: parseFloat(ad.total_revenue || 0),
          ctr: parseFloat(ad.ctr || 0),
          headline: ad.headline || creative?.headline,
          primary_text: ad.primary_text || creative?.primary_text,
          description: ad.description || creative?.description,
          audio_text_content: ad.audio_text_content || creative?.audio_text_content,
          text_overlay_content: ad.text_overlay_content || creative?.text_overlay_content
        });
      });
      
      // Add ads from creative data that don't have performance data in the date range
      // This ensures we show all ads from the card's ad_ids array, even if some have $0 spend in the date range
      // The card's ad_ids includes all ads with performance data, but some might have $0 in the selected date range
      creativeData.forEach(creative => {
        const adId = String(creative.raw_ad_id || creative.ad_id);
        if (!allAdsMap.has(adId)) {
          // Creative data now includes asset URLs
          allAdsMap.set(adId, {
            raw_ad_id: creative.raw_ad_id || creative.ad_id,
            ad_id: creative.raw_ad_id || creative.ad_id,
            ad_name: creative.ad_name || 'Unknown Ad',
            asset_url: creative.asset_url || creative.image_url || creative.video_url,
            image_url: creative.image_url || creative.asset_url,
            video_url: creative.video_url || creative.asset_url,
            media_format: creative.media_format || 'Unknown',
            roas: 0,
            total_spend: 0,
            total_revenue: 0,
            ctr: 0,
            headline: creative.headline,
            primary_text: creative.primary_text,
            description: creative.description,
            audio_text_content: creative.audio_text_content,
            text_overlay_content: creative.text_overlay_content
          });
        }
      });
      
      // Convert map to array
      const allAds = Array.from(allAdsMap.values());
      
      // Remove duplicates by raw_ad_id
      const uniqueAds = [];
      const seenIds = new Set();
      for (const ad of allAds) {
        const adId = ad.raw_ad_id || ad.ad_id;
        if (adId && !seenIds.has(String(adId))) {
          seenIds.add(String(adId));
          uniqueAds.push(ad);
        }
      }
      
      // Sort by performance metric if available
      uniqueAds.sort((a, b) => {
        const aValue = correlationMetric === 'roas' ? parseFloat(a.roas || 0) :
                      correlationMetric === 'spend' ? parseFloat(a.total_spend || 0) :
                      correlationMetric === 'revenue' ? parseFloat(a.total_revenue || 0) :
                      correlationMetric === 'ctr' ? parseFloat(a.ctr || 0) : 0;
        const bValue = correlationMetric === 'roas' ? parseFloat(b.roas || 0) :
                      correlationMetric === 'spend' ? parseFloat(b.total_spend || 0) :
                      correlationMetric === 'revenue' ? parseFloat(b.total_revenue || 0) :
                      correlationMetric === 'ctr' ? parseFloat(b.ctr || 0) : 0;
        return correlationMetric === 'cpa' ? aValue - bValue : bValue - aValue;
      });
      
      console.log('🔍 AdTypeAnalysis - Final result:', {
        creativeData: creativeData.length,
        performanceData: performanceData.length,
        finalAds: uniqueAds.length
      });
      
      // Update modal with fetched ads
      setAdsModal(prev => {
        if (prev.isOpen && prev.adType?.ad_type === adType.ad_type && prev.adType?.media_type === adType.media_type) {
          return {
            ...prev,
            ads: uniqueAds,
            loading: false
          };
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching ads for modal:', error);
      setAdsModal(prev => {
        if (prev.isOpen && prev.adType?.ad_type === adType.ad_type && prev.adType?.media_type === adType.media_type) {
          return {
            ...prev,
            loading: false
          };
        }
        return prev;
      });
    }
  };

  // Render thumbnail component
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-full h-full") => {
    if (!assetUrl) {
      // Use placeholder based on media type
      return (
        <div className={`${className} relative bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center`}>
          <div className="text-white text-4xl">📱</div>
          <div className="absolute top-2 left-2 bg-gray-600 text-white text-xs px-2 py-1 rounded font-medium">
            No Preview
          </div>
        </div>
      );
    }

    if (mediaFormat === 'Video' || assetUrl.includes('.mp4') || assetUrl.includes('.mov')) {
      return (
        <div className={`${className} relative bg-gray-200 rounded overflow-hidden`}>
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
            playsInline
            controls={false}
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
          <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded font-medium">
            Video
          </div>
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-sm hidden">
            <div className="text-center">
              <div className="text-2xl mb-2">📹</div>
              <div>Video Unavailable</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`${className} relative bg-gray-200 rounded overflow-hidden`}>
        <img
          src={assetUrl}
          alt={adName}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            if (e.target.nextSibling) {
              e.target.nextSibling.style.display = 'flex';
            }
          }}
        />
        <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded font-medium">
          Image
        </div>
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-sm hidden">
          <div className="text-center">
            <div className="text-2xl mb-2">🖼️</div>
            <div>Image Unavailable</div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Ad Type Analysis</h3>
            <p className="text-gray-600 mb-4">
              Analyzing ad performance across different types and media formats...
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 text-blue-400">ℹ️</div>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    This analysis includes industry benchmarks and may take 30-60 seconds to complete.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                📈 Ad Type Performance Analysis
              </h3>
              <p className="text-sm text-gray-500">Analyze performance across different ad formats and types</p>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-1.5 shadow-inner border border-gray-200">
              <button
                onClick={() => setViewMode('card')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'card'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setViewMode('time-trend')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'time-trend'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Time Trend
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Comparison
              </button>
            </div>
          </div>
          
          {/* Filter Status Indicators */}
          <div className="flex items-center flex-wrap gap-3 mt-3">
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 rounded-lg border border-indigo-200">
              <span className="text-xs font-semibold text-indigo-700">
                Ranking by: <span className="text-indigo-900">{correlationMetric.toUpperCase()}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200">
              <span className="text-xs font-semibold text-purple-700">
                Period: <span className="text-purple-900">Last {filters?.dateRange || 7} days</span>
              </span>
            </div>
            {filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0 && (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-pink-50 rounded-lg border border-pink-200">
                <span className="text-xs font-semibold text-pink-700">
                  Keywords: <span className="text-pink-900">{filters.adNameFilterKeywords.join(', ')}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* AI Summary Section - Right after title */}
        {filteredAdTypeData && filteredAdTypeData.length > 0 && (() => {
          // Get the actual displayed data (sorted by correlation metric)
          const validAdTypes = filteredAdTypeData.filter(type => {
            if (correlationMetric === 'revenue') return parseFloat(type.total_revenue || 0) > 0;
            if (correlationMetric === 'ctr') return parseFloat(type.ctr || 0) > 0;
            if (correlationMetric === 'spend') return parseFloat(type.total_spend || 0) > 0;
            if (correlationMetric === 'cpa') return parseFloat(type.cpa || 0) > 0;
            return parseFloat(type.roas || 0) > 0;
          });
          
          const sortedAdTypes = validAdTypes.sort((a, b) => {
            if (correlationMetric === 'revenue') return parseFloat(b.total_revenue || 0) - parseFloat(a.total_revenue || 0);
            if (correlationMetric === 'ctr') return parseFloat(b.ctr || 0) - parseFloat(a.ctr || 0);
            if (correlationMetric === 'spend') return parseFloat(b.total_spend || 0) - parseFloat(a.total_spend || 0);
            if (correlationMetric === 'cpa') return parseFloat(a.cpa || Infinity) - parseFloat(b.cpa || Infinity);
            return parseFloat(b.roas || 0) - parseFloat(a.roas || 0);
          });

          return (
            <ReportSummary 
              reportType="ad-type-analysis"
              reportData={sortedAdTypes}
              filters={filters}
              accountId={getAccountId()}
            />
          );
        })()}
        
        {filteredAdTypeData && filteredAdTypeData.length > 0 ? (
          <div className="space-y-6">
            {/* Summary Stats with Outlier Highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200">
                <div className="text-3xl font-bold text-blue-700 mb-1">
                  {filteredAdTypeData.length}
                </div>
                <div className="text-sm font-medium text-blue-800">Ad Types</div>
              </div>
              <div className="text-center p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200">
                <div className="text-3xl font-bold text-green-700 mb-1">
                  ${filteredAdTypeData.reduce((sum, type) => sum + parseFloat(type.total_spend || 0), 0).toLocaleString()}
                </div>
                <div className="text-sm font-medium text-green-800">Total Spend</div>
              </div>
              <div className="text-center p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200">
                <div className="text-3xl font-bold text-purple-700 mb-1">
                  {filteredAdTypeData.reduce((sum, type) => sum + parseInt(type.number_of_ads || 0), 0)}
                </div>
                <div className="text-sm font-medium text-purple-800">Total Ads</div>
              </div>
              <div className="text-center p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow-sm border border-yellow-200">
                <div className="text-3xl font-bold text-yellow-700 mb-1">
                  ${filteredAdTypeData.reduce((sum, type) => sum + parseFloat(type.total_revenue || 0), 0).toLocaleString()}
                </div>
                <div className="text-sm font-medium text-yellow-800">Total Revenue</div>
              </div>
            </div>

            {/* Card Grid View */}
            {viewMode === 'card' && (() => {
              const validAdTypes = filteredAdTypeData.filter(type => {
                if (correlationMetric === 'revenue') return parseFloat(type.total_revenue || 0) > 0;
                if (correlationMetric === 'ctr') return parseFloat(type.ctr || 0) > 0;
                if (correlationMetric === 'spend') return parseFloat(type.total_spend || 0) > 0;
                if (correlationMetric === 'cpa') return parseFloat(type.cpa || 0) > 0;
                return parseFloat(type.roas || 0) > 0;
              });
              
              const sortedAdTypes = validAdTypes.sort((a, b) => {
                if (correlationMetric === 'revenue') return parseFloat(b.total_revenue || 0) - parseFloat(a.total_revenue || 0);
                if (correlationMetric === 'ctr') return parseFloat(b.ctr || 0) - parseFloat(a.ctr || 0);
                if (correlationMetric === 'spend') return parseFloat(b.total_spend || 0) - parseFloat(a.total_spend || 0);
                if (correlationMetric === 'cpa') return parseFloat(a.cpa || Infinity) - parseFloat(b.cpa || Infinity);
                return parseFloat(b.roas || 0) - parseFloat(a.roas || 0);
              });
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {sortedAdTypes.map((type, index) => {
                    const isTop = isTopPerformer(type);
                    const isUnder = isUnderperformer(type);
                    const isOutlier = isTop || isUnder;
                    
                    return (
                      <div 
                        key={index} 
                        className={`relative rounded-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group ${
                          isTop 
                            ? 'bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border-2 border-emerald-300 shadow-lg ring-2 ring-emerald-200 ring-opacity-50' 
                            : isUnder
                            ? 'bg-gradient-to-br from-amber-50 via-white to-amber-50 border-2 border-amber-300 shadow-lg ring-2 ring-amber-200 ring-opacity-50'
                            : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
                        }`}
                      >
                        {/* Outlier Badge */}
                        {isOutlier && (
                          <div className={`absolute top-3 left-3 z-10 flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-bold shadow-lg ${
                            isTop 
                              ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white animate-pulse' 
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                          }`}>
                            {isTop ? (
                              <>
                                <TrendingUp className="w-3.5 h-3.5" />
                                <span>TOP</span>
                              </>
                            ) : (
                              <>
                                <TrendingDown className="w-3.5 h-3.5" />
                                <span>LOW</span>
                              </>
                            )}
                          </div>
                        )}

                        {/* Rank Badge for Top 3 */}
                        {index < 3 && !isOutlier && (
                          <div className="absolute top-3 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-bold shadow-lg">
                            {index + 1}
                          </div>
                        )}
                        {/* Media Preview with Gradient Overlay for Outliers */}
                        <div className={`relative aspect-square overflow-hidden ${
                          isTop ? 'bg-gradient-to-br from-emerald-100 to-emerald-50' :
                          isUnder ? 'bg-gradient-to-br from-amber-100 to-amber-50' :
                          'bg-gray-100'
                        }`}>
                          {isOutlier && (
                            <div className={`absolute inset-0 z-0 opacity-20 ${
                              isTop ? 'bg-gradient-to-br from-emerald-400 to-emerald-300' :
                              'bg-gradient-to-br from-amber-400 to-amber-300'
                            }`} />
                          )}
                          {(() => {
                            const key = `${type.ad_type}_${type.media_type}`;
                            const sampleAd = sampleAdsByType[key];
                            if (sampleAd && (sampleAd.asset_url || sampleAd.image_url || sampleAd.video_url)) {
                              const assetUrl = sampleAd.asset_url || sampleAd.image_url || sampleAd.video_url;
                              const mediaFormat = sampleAd.media_format || (assetUrl.includes('.mp4') || assetUrl.includes('.mov') ? 'Video' : 'Image');
                              return (
                                <div 
                                  className="w-full h-full cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewModal({
                                      isOpen: true,
                                      assetUrl: assetUrl,
                                      mediaFormat: mediaFormat,
                                      adName: sampleAd.ad_name || type.ad_type
                                    });
                                  }}
                                  title="Click to preview"
                                >
                                  {renderThumbnail(assetUrl, mediaFormat, sampleAd.ad_name || type.ad_type, "w-full h-full")}
                                </div>
                              );
                            }
                            return renderThumbnail(null, type.media_type || 'Unknown', type.ad_type, "w-full h-full");
                          })()}
                        </div>
                        
                        {/* Card Content */}
                        <div className={`p-5 ${isOutlier ? 'bg-white/80 backdrop-blur-sm' : ''}`}>
                          {/* Ad Type Name */}
                          <h4 className={`font-bold text-base mb-3 line-clamp-1 ${
                            isTop ? 'text-emerald-900' :
                            isUnder ? 'text-amber-900' :
                            'text-gray-900'
                          }`} title={type.ad_type}>
                            {type.ad_type || 'Unknown Type'}
                          </h4>
                          
                          {/* Media Type Badge */}
                          {type.media_type && type.media_type !== 'Unknown' && (
                            <div className="mb-3">
                              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                                type.media_type === 'Video' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                                type.media_type === 'Image' ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' :
                                'bg-gray-200 text-gray-700'
                              }`}>
                                {type.media_type}
                              </span>
                            </div>
                          )}
                          
                          {/* Metrics Tags */}
                          <div className="space-y-3">
                            {/* Primary Metrics Row */}
                            <div className="flex flex-wrap gap-2">
                              <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                                correlationMetric === 'roas' 
                                  ? isTop 
                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white' 
                                    : isUnder
                                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                                    : 'bg-green-100 text-green-800 border-2 border-green-300'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                <span className="mr-1.5">ROAS:</span> 
                                <span className="text-sm">{parseFloat(type.roas || 0).toFixed(2)}x</span>
                              </div>
                              <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                                correlationMetric === 'spend' 
                                  ? isTop 
                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white' 
                                    : isUnder
                                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                                    : 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                <span className="mr-1.5">Spend:</span> 
                                <span className="text-sm">${parseFloat(type.total_spend || 0).toFixed(0)}</span>
                              </div>
                            </div>
                            
                            {/* Secondary Metrics Row */}
                            <div className="flex flex-wrap gap-2">
                              <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                                correlationMetric === 'ctr' 
                                  ? isTop 
                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white' 
                                    : isUnder
                                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                                    : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                <span className="mr-1.5">CTR:</span> 
                                <span className="text-sm">{parseFloat(type.ctr || 0).toFixed(2)}%</span>
                              </div>
                              <div className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-700 shadow-sm">
                                <span className="mr-1.5">Ads:</span> 
                                <span className="text-sm">{parseInt(type.number_of_ads || 0).toLocaleString()}</span>
                              </div>
                            </div>
                            
                            {/* Revenue */}
                            <div className={`flex flex-wrap gap-2 text-xs font-semibold ${
                              isTop ? 'text-emerald-700' :
                              isUnder ? 'text-amber-700' :
                              'text-gray-600'
                            }`}>
                              <div className="px-2 py-1 bg-gray-50 rounded-lg">
                                <span className="mr-1">Revenue:</span> 
                                <span className="text-sm">${parseFloat(type.total_revenue || 0).toFixed(0)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Folder Button - Bottom Right with Enhanced Styling */}
                        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openAdsModal(type);
                            }}
                            className={`p-3 rounded-full shadow-xl pointer-events-auto transition-all duration-300 ${
                              isTop 
                                ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white' 
                                : isUnder
                                ? 'bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white'
                                : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                            }`}
                            title="View all ads in this type"
                          >
                            <Folder className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <p className="text-gray-500 text-lg mb-2">No ad type data available</p>
            <p className="text-gray-400 text-sm">
              No ads found for the selected date range and filters.
            </p>
            {adTypeData && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                <strong>Debug Info:</strong> Ad type data: {JSON.stringify(adTypeData, null, 2)}
              </div>
            )}
          </div>
        )}

        {/* Comparison Chart View */}
        {viewMode === 'comparison' && !loading && filteredAdTypeData && filteredAdTypeData.length > 0 && (
          <div className="mt-6">
            <ComparisonChart
              title="Ad Types Comparison"
              data={filteredAdTypeData.map(type => ({
                name: `${type.ad_type} (${type.media_type})`,
                spend: parseFloat(type.total_spend || 0),
                roas: parseFloat(type.roas || 0),
                ctr: parseFloat(type.ctr || 0),
                revenue: parseFloat(type.total_revenue || 0)
              }))}
              xAxisKey="name"
              metrics={[
                { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
              ]}
              height={400}
              maxItems={15}
            />
          </div>
        )}

        {/* Time Series Performance Chart View */}
        {viewMode === 'time-trend' && !loading && adTypeData && adTypeData.length > 0 && (() => {
          const groups = adTypeData.map(type => ({
            name: `${type.ad_type} (${type.media_type})`,
            adIds: type.ad_ids || []
          })).filter(group => group.adIds && group.adIds.length > 0);
          
          return groups.length > 0 && (
            <div className="mt-6">
              <GroupTimeSeriesChart 
                accountId={getAccountId()}
                groups={groups}
                dateRangeDays={filters?.dateRange || 7}
                numberOfPeriods={14}
                groupType="Ad Type"
                startDate={filters?.dateRangeType === 'custom' ? filters?.startDate : null}
                endDate={filters?.dateRangeType === 'custom' ? filters?.endDate : null}
              />
            </div>
          );
        })()}
      </div>
      
      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-4" title={previewModal.adName}>
                {previewModal.adName}
              </h3>
              <button 
                onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}
                className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
                title="Close"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {previewModal.mediaFormat === 'Video' ? (
                <video 
                  src={previewModal.assetUrl} 
                  controls 
                  className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                  autoPlay
                  preload="metadata"
                >
                  <source src={previewModal.assetUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={previewModal.assetUrl} 
                  alt={previewModal.adName}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(previewModal.assetUrl).then(() => {
                        // Show a temporary success message
                        const btn = document.activeElement;
                        const originalText = btn.textContent;
                        btn.textContent = '✓ Copied!';
                        setTimeout(() => {
                          btn.textContent = originalText;
                        }, 2000);
                      }).catch(err => {
                        console.error('Failed to copy URL:', err);
                        alert('Failed to copy URL. Please try again.');
                      });
                    }}
                    className="px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    title="Copy asset URL to clipboard"
                  >
                    Copy URL
                  </button>
                  <button
                    onClick={() => setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' })}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Ads Modal */}
      {adsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setAdsModal({ isOpen: false, adType: null, ads: [], loading: false })}>
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  All Ads: {adsModal.adType?.ad_type || 'Unknown Type'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {adsModal.ads.length} ads • {adsModal.adType?.media_type || 'All'} format
                </p>
              </div>
              <button 
                onClick={() => setAdsModal({ isOpen: false, adType: null, ads: [], loading: false })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {/* Content - Grid of Ads */}
            <div className="flex-1 overflow-y-auto p-6">
              {adsModal.loading && adsModal.ads.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading ads...</p>
                  </div>
                </div>
              ) : adsModal.ads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {adsModal.ads.map((ad, index) => {
                    const assetUrl = ad.asset_url || ad.image_url || ad.video_url;
                    const mediaFormat = ad.media_format || (assetUrl?.includes('.mp4') || assetUrl?.includes('.mov') ? 'Video' : 'Image');
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all bg-white">
                        {/* Media Preview */}
                        {assetUrl && (
                          <div 
                            className="relative bg-gray-100 aspect-square cursor-pointer"
                            onClick={() => {
                              setPreviewModal({
                                isOpen: true,
                                assetUrl: assetUrl,
                                mediaFormat: mediaFormat,
                                adName: ad.ad_name || 'Unknown Ad'
                              });
                            }}
                            title="Click to preview"
                          >
                            {renderThumbnail(assetUrl, mediaFormat, ad.ad_name || 'Unknown Ad', "w-full h-full")}
                          </div>
                        )}
                        
                        {/* Ad Info */}
                        <div className="p-4">
                          <h4 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2" title={ad.ad_name}>
                            {ad.ad_name || 'Unknown Ad'}
                          </h4>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">ROAS:</span> {parseFloat(ad.roas || 0).toFixed(2)}x
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">Spend:</span> ${parseFloat(ad.total_spend || 0).toFixed(0)}
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">CTR:</span> {parseFloat(ad.ctr || 0).toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">📊</div>
                  <p className="text-gray-500 text-lg">No ads found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdTypeAnalysis;